import type { Building, Resources } from '@/models/types';
import { BuildingManager } from '@/systems/BuildingManager';
import { ResourceManager } from '@/systems/ResourceManager';
import { formatNumber, formatCostShort } from '@/utils/format';
import { BUILDING_ICONS } from '@/models/building-icons';

export class BuildingPanel {
  private container: HTMLElement;
  private rows: Map<string, {
    row: HTMLElement;
    levelEl: HTMLElement;
    costEl: HTMLElement;
    prodEl: HTMLElement;
    synergyEl: HTMLElement;
    btn: HTMLButtonElement;
  }> = new Map();
  private onUpgrade: (buildingId: string) => void;

  constructor(parent: HTMLElement, buildings: readonly Building[], onUpgrade: (id: string) => void) {
    this.onUpgrade = onUpgrade;

    this.container = document.createElement('div');
    this.container.id = 'building-panel';
    this.container.style.cssText = `
      position: absolute; bottom: 0; left: 0; width: 100%;
      max-height: 45vh; overflow-y: auto;
    `;

    for (const b of buildings) {
      const row = document.createElement('div');
      row.className = 'building-row';

      const icon = document.createElement('div');
      icon.className = 'building-icon';
      icon.textContent = BUILDING_ICONS[b.id] ?? '🏗️';

      const info = document.createElement('div');
      info.className = 'building-info';

      const nameRow = document.createElement('div');
      const nameSpan = document.createElement('span');
      nameSpan.className = 'building-name';
      nameSpan.textContent = b.name;
      const levelSpan = document.createElement('span');
      levelSpan.className = 'building-level';
      nameRow.appendChild(nameSpan);
      nameRow.appendChild(levelSpan);

      const prodEl = document.createElement('div');
      prodEl.className = 'building-prod';

      const costEl = document.createElement('div');
      costEl.className = 'building-cost';

      const synergyEl = document.createElement('div');
      synergyEl.className = 'building-synergy';

      info.appendChild(nameRow);
      info.appendChild(prodEl);
      info.appendChild(synergyEl);
      info.appendChild(costEl);

      const btn = document.createElement('button');
      btn.className = 'btn btn-upgrade';
      btn.textContent = '▲ UP';
      btn.addEventListener('click', () => {
        this.onUpgrade(b.id);
        row.classList.remove('upgrade-flash');
        void row.offsetWidth;
        row.classList.add('upgrade-flash');
      });

      row.appendChild(icon);
      row.appendChild(info);
      row.appendChild(btn);
      this.container.appendChild(row);

      this.rows.set(b.id, { row, levelEl: levelSpan, costEl, prodEl, synergyEl, btn });
    }

    parent.appendChild(this.container);
  }

  update(buildingManager: BuildingManager, resources: ResourceManager): void {
    for (const [id, els] of this.rows) {
      const level = buildingManager.getLevel(id);
      const cost = buildingManager.getUpgradeCost(id);
      const canAfford = resources.canAfford(cost);
      const building = buildingManager.getBuildings().find(b => b.id === id);
      const synergyBonus = buildingManager.getSynergyBonus(id);
      const prod = building ? building.baseProduction * level * (1 + synergyBonus) : 0;

      els.levelEl.textContent = level > 0 ? ` Lv.${level}` : '';
      els.prodEl.textContent = prod > 0 ? `+${formatNumber(prod)}/s` : 'not built';
      if (synergyBonus > 0) {
        els.synergyEl.textContent = `+${Math.round(synergyBonus * 100)}% synergy`;
      } else {
        els.synergyEl.textContent = '';
      }
      els.costEl.textContent = formatCostShort(cost);
      els.btn.disabled = !canAfford;
    }
  }

  destroy(): void {
    this.container.remove();
  }
}
